/**
 * HomepageCategoryCurator — iter42 admin-only "Shop By Category" curation.
 *
 * Replaces the hardcoded CATEGORIES list with an admin-controlled, ordered
 * array of `{name, image_url, unit, enabled, order}` items.  Customers see
 * only `enabled` rows on the homepage in `order` ascending.
 *
 * Operations supported (all local; saved on the global Save bar):
 *   - Rename a category
 *   - Toggle enabled/disabled
 *   - Reorder via ↑ / ↓
 *   - Edit image_url (or click "Pick from Cloudinary" → opens picker)
 *   - Add a new row
 *   - Remove a row
 */
import { ArrowUp, ArrowDown, Plus, Trash2, Tags, Cloud } from "lucide-react";
import { imageUrl } from "../../lib/cloudinary";

export default function HomepageCategoryCurator({ items, onChange, openPicker }) {
  const update = (i, patch) => onChange(items.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const arr = [...items];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr.map((c, idx) => ({ ...c, order: idx })));
  };
  const add = () => onChange([...items, { name: "", image_url: "", unit: "products", enabled: true, order: items.length }]);

  return (
    <section className="bg-white border border-brand-line rounded-2xl p-5 sm:p-6" data-testid="admin-homepage-categories">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl text-brand-dark flex items-center gap-2">
          <Tags className="w-5 h-5 text-brand-chili" strokeWidth={1.6} />
          Shop By Category
        </h2>
        <button type="button" onClick={add} className="btn-secondary inline-flex items-center text-xs" data-testid="hp-cat-add">
          <Plus className="w-3.5 h-3.5 mr-1" strokeWidth={1.7} /> Add category
        </button>
      </div>

      {items.length === 0 && (
        <div className="text-xs text-brand-muted bg-brand-cream/60 border border-dashed border-brand-line rounded-lg p-4">
          No categories curated yet. Click <strong>Add category</strong> to start. Until then, bundled defaults are shown on the homepage.
        </div>
      )}

      <ul className="space-y-3" data-testid="hp-cat-list">
        {items.map((c, i) => (
          <li key={i} className="grid grid-cols-12 gap-3 items-start bg-brand-cream/40 rounded-lg p-3" data-testid={`hp-cat-row-${i}`}>
            {/* Thumbnail */}
            <div className="col-span-3 sm:col-span-2">
              {c.image_url ? (
                <img src={imageUrl(c.image_url, { width: 120, height: 120, crop: "fill" })} alt="" className="w-full aspect-square rounded-md object-cover bg-white" />
              ) : (
                <div className="w-full aspect-square bg-white border border-dashed border-brand-line rounded-md flex items-center justify-center text-[10px] text-brand-muted text-center">No image</div>
              )}
            </div>
            {/* Fields */}
            <div className="col-span-9 sm:col-span-10 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input className="input-field text-sm" placeholder="Category name (e.g. Pickles)" value={c.name || ""} onChange={(e) => update(i, { name: e.target.value })} data-testid={`hp-cat-name-${i}`} />
              <input className="input-field text-sm" placeholder="Unit (products, jars, packs)" value={c.unit || ""} onChange={(e) => update(i, { unit: e.target.value })} data-testid={`hp-cat-unit-${i}`} />
              <input className="input-field text-sm sm:col-span-2" placeholder="Image URL — paste a Cloudinary URL or use the picker" value={c.image_url || ""} onChange={(e) => update(i, { image_url: e.target.value })} data-testid={`hp-cat-image-${i}`} />
              <div className="sm:col-span-2 flex flex-wrap items-center gap-2 mt-1">
                <button type="button" onClick={() => openPicker(i)} className="text-xs text-brand-dark inline-flex items-center hover:text-brand-chili" data-testid={`hp-cat-pick-${i}`}>
                  <Cloud className="w-3 h-3 mr-1" /> Pick from Cloudinary
                </button>
                <label className="text-xs inline-flex items-center gap-1.5 ml-auto cursor-pointer">
                  <input type="checkbox" checked={c.enabled !== false} onChange={(e) => update(i, { enabled: e.target.checked })} className="w-3.5 h-3.5 accent-brand-chili" data-testid={`hp-cat-enabled-${i}`} />
                  <span>Show on homepage</span>
                </label>
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 rounded-md hover:bg-white disabled:opacity-30" data-testid={`hp-cat-up-${i}`}><ArrowUp className="w-3.5 h-3.5" strokeWidth={1.7} /></button>
                <button type="button" onClick={() => move(i, +1)} disabled={i === items.length - 1} className="p-1.5 rounded-md hover:bg-white disabled:opacity-30" data-testid={`hp-cat-down-${i}`}><ArrowDown className="w-3.5 h-3.5" strokeWidth={1.7} /></button>
                <button type="button" onClick={() => remove(i)} className="p-1.5 rounded-md hover:bg-white text-brand-chili" data-testid={`hp-cat-remove-${i}`}><Trash2 className="w-3.5 h-3.5" strokeWidth={1.7} /></button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
